import { useEffect, useRef, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";

export interface ContactFields {
  email: string;
  phone: string;
  address: string;
}

interface ProfileContactModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  displayName?: string;
  initial: ContactFields;
  showAddress?: boolean;
  onSave: (fields: ContactFields) => Promise<boolean>;
}

// Modal de edición de datos de contacto. El propio usuario actualiza su correo
// y teléfono; un administrador puede actualizar correo, teléfono y dirección de
// cualquier cuenta.
const ProfileContactModal = ({
  open,
  onClose,
  title,
  subtitle,
  displayName,
  initial,
  showAddress = false,
  onSave,
}: ProfileContactModalProps) => {
  const [form, setForm] = useState<ContactFields>(initial);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const initialRef = useRef(initial);
  initialRef.current = initial;

  useEffect(() => {
    if (open) {
      setForm(initialRef.current);
      setError("");
      setSaving(false);
    }
  }, [open]);

  const set = (patch: Partial<ContactFields>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const submit = async () => {
    if (!form.email.trim() && !form.phone.trim()) {
      setError("Ingresa al menos un correo o un teléfono de contacto.");
      return;
    }
    setSaving(true);
    const ok = await onSave({
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
    });
    setSaving(false);
    if (!ok) {
      setError("No se pudieron guardar los datos. Intenta de nuevo.");
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      icon="ri-contacts-line"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} disabled={saving} className={primaryButtonClass}>
            {saving ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-save-3-line" />
            )}{" "}
            Guardar datos
          </button>
        </>
      }
    >
      {displayName && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-accent-500/25 bg-background-200/30 px-3.5 py-3">
          <span className="w-9 h-9 rounded-full bg-primary-500/25 text-accent-200 flex items-center justify-center shrink-0 font-semibold">
            {displayName.charAt(0).toUpperCase() || "?"}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 truncate">{displayName}</p>
            {subtitle && <p className="text-[11px] text-foreground-500">{subtitle}</p>}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className={labelClass}>Correo electrónico</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
              <i className="ri-mail-line" />
            </span>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set({ email: e.target.value })}
              placeholder="correo@ejemplo.com"
              className={`${inputClass} pl-9`}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Teléfono / WhatsApp</label>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
              <i className="ri-phone-line" />
            </span>
            <input
              value={form.phone}
              onChange={(e) => set({ phone: e.target.value })}
              placeholder="300 000 0000"
              className={`${inputClass} pl-9`}
            />
          </div>
        </div>

        {showAddress && (
          <div>
            <label className={labelClass}>Dirección</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
                <i className="ri-map-pin-line" />
              </span>
              <input
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
                placeholder="Dirección de entrega / residencia"
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="text-[11px] text-primary-200 mt-3 flex items-center gap-1.5">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}
    </Modal>
  );
};

export default ProfileContactModal;